<div class="wizard-container">
  <!-- Toast Notifications -->
  <p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true" />

  <!-- Live Region for Announcements -->
  <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
    {{ liveRegionMessage() }}
  </div>

  <app-validation-error-summary
    [sections]="validationErrorSections()"
    (closed)="validationErrorSections.set([])"
  />

  <!-- Modification Request Banner -->
  @if (hasModificationRequest()) {
    <div class="modification-request-banner mb-4 p-4 border-round-lg surface-0 border-2 border-orange-500 shadow-3" role="alert" aria-live="assertive">
      <div class="flex align-items-start gap-3">
        <div class="flex-shrink-0">
          <i class="pi pi-exclamation-triangle text-orange-500 text-3xl" aria-hidden="true"></i>
        </div>
        <div class="flex-1">
          <h3 class="text-orange-900 font-bold text-xl m-0 mb-2">
            <i class="pi pi-refresh mr-2" aria-hidden="true"></i>
            Richiesta di Modifica
          </h3>
          <p class="text-orange-800 text-base m-0 mb-3">
            AXA ha richiesto delle modifiche alla tua registrazione. Si prega di leggere attentamente le note seguenti e apportare le correzioni necessarie.
          </p>
          @if (modificationComment()) {
            <div class="surface-50 border-round p-3 border-left-3 border-orange-500">
              <p class="text-sm font-semibold text-600 m-0 mb-2">
                <i class="pi pi-comment mr-2" aria-hidden="true"></i>
                Note del Revisore:
              </p>
              <p class="text-base text-900 m-0 white-space-pre-wrap">{{ displayedComment() }}</p>
              @if (isCommentLong()) {
                <button 
                  type="button"
                  class="text-sm text-orange-600 hover:text-orange-700 font-semibold cursor-pointer border-none bg-transparent p-0 mt-2"
                  (click)="toggleModificationNotes()"
                  [attr.aria-expanded]="isModificationNotesExpanded()"
                  aria-controls="modification-notes">
                  <i class="pi mr-1" [class.pi-chevron-down]="!isModificationNotesExpanded()" [class.pi-chevron-up]="isModificationNotesExpanded()" aria-hidden="true"></i>
                  {{ isModificationNotesExpanded() ? 'Mostra meno' : 'Visualizza altro' }}
                </button>
              }
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Page Header (hidden in review mode) -->
  @if (!professionalIdInput()) {
    <section class="page-header mb-5" role="region" aria-labelledby="form-title">
      <div class="header-content">
        <div class="header-left">
          <i class="pi pi-user header-icon" aria-hidden="true"></i>
          <div class="header-text">
            <h1 id="form-title">Dati Generali</h1>
            <p class="professional-info">
              @if (professionalSummaryBusinessName()) {
                <strong>{{ professionalSummaryBusinessName() }}</strong><br>
              }
              @if (professionalIdDisplay()) {
                ID: {{ professionalIdDisplay() }}
              }
              @if (professionalSummaryType()) {
                &nbsp;·&nbsp;{{ professionalSummaryType() }}
              }
            </p>
            <p class="subtitle">Inserisci i dati professionali e di contatto</p>
          </div>
        </div>
        <!-- Completion Indicator -->
        <div class="completion-badge" [attr.aria-label]="isStepCompleted() ? 'Step completato' : 'Step non completato'">
          @if (isStepCompleted()) {
            <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
            <span class="completion-text">Completato</span>
          }
          @else {
            <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
            <span class="completion-text">Da completare</span>
          }
        </div>
      </div>
    </section>
  }

  <!-- Read-Only Mode Banner (hidden in review mode) -->
  @if (isReadOnly() && !professionalIdInput()) {
    <div class="read-only-banner mb-4" role="alert">
      <div class="flex align-items-center gap-2 mb-2">
        <i class="pi pi-eye text-blue-600 text-xl" aria-hidden="true"></i>
        <strong class="text-blue-900">Modalità Sola Lettura</strong>
      </div>
      <span class="text-blue-700 text-sm">Stai visualizzando l'anagrafica inviata. I campi non sono modificabili.</span>
    </div>
  }

  <!-- Instructions for accessibility -->
  @if (!isReadOnly()) {
    <div class="surface-100 border-round p-3 mb-4" role="region" aria-labelledby="form-instructions-title">
      <p id="form-instructions-title" class="text-sm text-700 m-0">
        <i class="pi pi-info-circle mr-2" aria-hidden="true"></i>
        <strong>Istruzioni:</strong> Compila tutti i campi contrassegnati con asterisco (*). I campi disabilitati sono precompilati dal sistema.
      </p>
    </div>
  }

  <!-- Step Content -->
  <div class="step-content" [class.read-only-mode]="isReadOnly()">
    <!-- SEZIONE 1: DATI ISTITUZIONALI -->
    <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-dati-istituzionali">
      <div class="institutional-section-header flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
        <i class="pi pi-building text-2xl flex-shrink-0" style="color: #00125b;" aria-hidden="true" title="Icona Dati Istituzionali"></i>
        <h2 id="section-dati-istituzionali" class="text-xl font-semibold m-0" style="color: #00125b;">Dati Istituzionali</h2>
        @if (preRegTypeLabel() || preRegType()) {
          <span class="professional-category-pill">
            <span class="visually-hidden">Categoria fiduciario: </span>
            {{ preRegTypeLabel() || preRegType() }}
          </span>
        }
      </div>

      <!-- Sottosezione: Dati Istituzionali Generali -->
      <div class="surface-0 border-round p-4 mb-4 border-1 border-200">
        <h3 class="text-lg font-semibold m-0 mb-4">
          <i class="pi pi-id-card mr-2 text-primary" aria-hidden="true"></i>
          Dati Istituzionali Generali
        </h3>

      <div class="grid">
        <!-- 2. Categoria Carrozzeria (solo se BODYSHOP) -->
        @if (preRegBodyShopType()) {
          <div class="field col-12 md:col-4">
            <label for="prereg-carrozzeria" class="font-semibold block mb-2">CATEGORIA CARROZZERIA</label>
            <input
              id="prereg-carrozzeria"
              type="text"
              pInputText
              [value]="preRegBodyShopTypeLabel() || preRegBodyShopType()"
              [disabled]="true"
              [attr.aria-readonly]="true"
              class="w-full"
            />
          </div>
        }

        <!-- 3. Persona Giuridica o Fisica -->
        <div class="field col-12 md:col-4">
          <label for="persona-giuridica" class="font-semibold block mb-2">
            PERSONA FISICA / GIURIDICA <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="persona-giuridica"
            [(ngModel)]="legalOrPhysical"
            (ngModelChange)="onPersonTypeChange($event)"
            [options]="personTypeOptions()"
            optionLabel="label"
            optionValue="value"
            [showClear]="false"
            [disabled]="isReadOnly()"
            styleClass="w-full"
            [attr.aria-required]="true"
          />
        </div>

        <!-- 5. Ragione Sociale -->
        <div class="field col-12 md:col-4">
          <label for="ragione-sociale" class="font-semibold block mb-2">
            RAGIONE SOCIALE <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="ragione-sociale"
            type="text"
            pInputText
            [(ngModel)]="businessName"
            (blur)="markFieldTouched('businessName')"
            [disabled]="isReadOnly()"
            autocomplete="organization"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('businessName') && isMissingMeaningfulText(businessName())"
            [attr.aria-describedby]="isFieldTouched('businessName') && isMissingMeaningfulText(businessName()) ? 'ragione-sociale-error' : null"
            class="w-full"
          />
          @if (isFieldTouched('businessName') && isMissingMeaningfulText(businessName())) {
            <small id="ragione-sociale-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
          <small class="block mt-1 text-600">Non inserire il tipo di società</small>
        </div>

        <!-- 6. Tipo Società (Filtered) -->
        <div class="field col-12 md:col-4">
          <label for="tipo-societa" class="font-semibold block mb-2">
            TIPO SOCIETÀ <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="tipo-societa"
            [(ngModel)]="companyType"
            [options]="filteredSupplierTypes()"
            optionLabel="label"
            optionValue="value"
            optionDisabled="disabled"
            panelStyleClass="company-type-select-panel"
            placeholder="-- Seleziona --"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full"
            (blur)="markFieldTouched('companyType')"
            [attr.aria-required]="true"
            [attr.aria-disabled]="!legalOrPhysical()"
            [attr.aria-invalid]="isFieldTouched('companyType') && !companyType()"
            [attr.aria-describedby]="isFieldTouched('companyType') && !companyType() ? 'tipo-societa-error' : null"
          />
          @if (isFieldTouched('companyType') && !companyType()) {
            <small id="tipo-societa-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>

        <!-- 7. Partita IVA -->
        <div class="field col-12 md:col-4">
          <label for="partita-iva" class="font-semibold block mb-2">
            PARTITA IVA
            @if (legalOrPhysical() === 'GIURIDICA') {
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            }
          </label>
          <input
            id="partita-iva"
            type="text"
            pInputText
            [(ngModel)]="vatNumber"
            (ngModelChange)="onVatNumberChange($event)"
            (blur)="markFieldTouched('vatNumber')"
            [disabled]="isReadOnly()"
            autocomplete="off"
            inputmode="numeric"
            placeholder="es. 12345678901"
            class="w-full"
            pattern="[0-9]{11}"
            [required]="legalOrPhysical() === 'GIURIDICA'"
            maxlength="11"
            [class.p-invalid]="isFieldTouched('vatNumber') && ((legalOrPhysical() === 'GIURIDICA' && isMissingMeaningfulText(vatNumber())) || (hasMeaningfulText(vatNumber()) && !isValidVatNumber(vatNumber())))"
            [attr.aria-required]="legalOrPhysical() === 'GIURIDICA'"
            [attr.aria-invalid]="isFieldTouched('vatNumber') && ((legalOrPhysical() === 'GIURIDICA' && isMissingMeaningfulText(vatNumber())) || (hasMeaningfulText(vatNumber()) && !isValidVatNumber(vatNumber())))"
            [attr.aria-describedby]="isFieldTouched('vatNumber') && ((legalOrPhysical() === 'GIURIDICA' && isMissingMeaningfulText(vatNumber())) || (hasMeaningfulText(vatNumber()) && !isValidVatNumber(vatNumber()))) ? 'partita-iva-error' : null"
          />
          @if (isFieldTouched('vatNumber') && legalOrPhysical() === 'GIURIDICA' && isMissingMeaningfulText(vatNumber())) {
            <small id="partita-iva-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          } @else if (isFieldTouched('vatNumber') && hasMeaningfulText(vatNumber()) && !isValidVatNumber(vatNumber())) {
            <small id="partita-iva-error" class="text-red-500" role="alert">Formato non valido. Inserire esattamente 11 cifre numeriche</small>
          }
        </div>

        <!-- 8. Codice Fiscale -->
        <div class="field col-12 md:col-4">
          <label for="codice-fiscale" class="font-semibold block mb-2">
            CODICE FISCALE
            @if (legalOrPhysical() === 'FISICA') {
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            }
          </label>
          <input
            id="codice-fiscale"
            type="text"
            pInputText
            [value]="fiscalCode()"
            (input)="onTaxCodeChange($any($event.target).value)"
            maxlength="16"
            [disabled]="isReadOnly()"
            (blur)="markFieldTouched('fiscalCode')"
            [class.p-invalid]="fiscalCodeError() || (isFieldTouched('fiscalCode') && legalOrPhysical() === 'FISICA' && isMissingMeaningfulText(fiscalCode()))"
            [attr.aria-invalid]="fiscalCodeError() || (isFieldTouched('fiscalCode') && legalOrPhysical() === 'FISICA' && isMissingMeaningfulText(fiscalCode())) ? 'true' : null"
            [attr.aria-describedby]="fiscalCodeError() || (isFieldTouched('fiscalCode') && legalOrPhysical() === 'FISICA' && isMissingMeaningfulText(fiscalCode())) ? 'codice-fiscale-error' : null"
            [attr.aria-required]="legalOrPhysical() === 'FISICA'"
            [required]="legalOrPhysical() === 'FISICA'"
            autocomplete="off"
            class="w-full"
          />
          @if (isFieldTouched('fiscalCode') && legalOrPhysical() === 'FISICA' && isMissingMeaningfulText(fiscalCode()) && !fiscalCodeError()) {
            <small id="codice-fiscale-error" class="text-red-500" role="alert" aria-live="polite">Campo obbligatorio</small>
          } @else if (fiscalCodeError()) {
            <small id="codice-fiscale-error" class="text-red-500" role="alert" aria-live="polite">
              {{ fiscalCodeError() }}
            </small>
          }
        </div>
      </div>
      </div>

      @if (!isOperationalLocationMode()) {
      <!-- Sottosezione: Indirizzo Sede Legale -->
      <div class="surface-0 border-round p-4 mb-4 border-1 border-200">
        <h3 class="text-lg font-semibold m-0 mb-4">
          <i class="pi pi-map-marker mr-2 text-primary" aria-hidden="true"></i>
          Indirizzo Sede Legale
        </h3>

      <div class="grid">
        <div class="field col-12 md:col-4">
          <label for="tipo-via" class="font-semibold block mb-2">
            TIPO VIA <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="tipo-via"
            [options]="streetTypeOptions()"
            [(ngModel)]="streetType"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona --"
            [showClear]="true"
            styleClass="w-full"
            (ngModelChange)="onLegalAddressValueChange()"
            (blur)="markFieldTouched('streetType')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('streetType') && !streetType()"
            [attr.aria-describedby]="isFieldTouched('streetType') && !streetType() ? 'tipo-via-error' : null"
          />
          @if (isFieldTouched('streetType') && !streetType()) {
            <small id="tipo-via-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        <div class="field col-12 md:col-4">
          <label for="nome-via" class="font-semibold block mb-2">
            NOME VIA <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="nome-via"
            type="text"
            pInputText
            [(ngModel)]="streetName"
            placeholder="Descrizione libera"
            class="w-full"
            (ngModelChange)="onLegalAddressValueChange()"
            (blur)="markFieldTouched('streetName')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('streetName') && (!streetName() || streetName().trim() === '')"
            [attr.aria-describedby]="isFieldTouched('streetName') && (!streetName() || streetName().trim() === '') ? 'nome-via-error' : null"
            autocomplete="address-line1"
          />
          @if (isFieldTouched('streetName') && (!streetName() || streetName().trim() === '')) {
            <small id="nome-via-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        <div class="field col-12 md:col-4">
          <label for="numero" class="font-semibold block mb-2">
            CIVICO <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="numero"
            type="text"
            pInputText
            [(ngModel)]="streetNumber"
            placeholder="es. 7 o 7/A"
            class="w-full"
            [disabled]="isReadOnly()"
            (ngModelChange)="onLegalAddressValueChange()"
            (blur)="markFieldTouched('streetNumber')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('streetNumber') && !streetNumber()"
            [attr.aria-describedby]="isFieldTouched('streetNumber') && !streetNumber() ? 'numero-error' : null"
          />
          @if (isFieldTouched('streetNumber') && !streetNumber()) {
            <small id="numero-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        <div class="field col-12 md:col-4">
          <label for="cap" class="font-semibold block mb-2">
            CAP <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            type="text"
            id="cap"
            pInputText
            [(ngModel)]="zipCode"
            maxlength="5"
            placeholder="5 cifre"
            class="w-full"
            [disabled]="isReadOnly()"
            (ngModelChange)="onLegalAddressValueChange()"
            (blur)="markFieldTouched('zipCode')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('zipCode') && !zipCode()"
            [attr.aria-describedby]="isFieldTouched('zipCode') && !zipCode() ? 'cap-error' : null"
          />
          @if (isFieldTouched('zipCode') && !zipCode()) {
            <small id="cap-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        
        <!-- 1. REGIONE (First in hierarchy) -->
        <div class="field col-12 md:col-4">
          <label for="regione" class="font-semibold block mb-2">
            REGIONE <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="regione"
            [options]="regionOptions()"
            [(ngModel)]="region"
            (ngModelChange)="onRegionChange($event); onLegalAddressChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona Regione --"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full"
            (blur)="markFieldTouched('region')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('region') && !region()"
            [attr.aria-describedby]="isFieldTouched('region') && !region() ? 'regione-error' : null"
          />
          @if (isFieldTouched('region') && !region()) {
            <small id="regione-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        
        <!-- 2. PROVINCIA (Filtered by Region) -->
        <div class="field col-12 md:col-4">
          <label for="provincia" class="font-semibold block mb-2">
            PROVINCIA <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="provincia"
            [options]="provinceOptions()"
            [(ngModel)]="province"
            (ngModelChange)="onProvinceChange($event); onLegalAddressChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona Provincia --"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full"
            (blur)="markFieldTouched('province')"
            [attr.aria-required]="true"
            [attr.aria-disabled]="isProvinceDisabled()"
            [attr.aria-invalid]="isFieldTouched('province') && !province()"
            [attr.aria-describedby]="isFieldTouched('province') && !province() ? 'provincia-error' : null"
          />
          @if (isFieldTouched('province') && !province()) {
            <small id="provincia-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
          @if (isProvinceDisabled()) {
            <small class="text-color-secondary">Seleziona prima la regione</small>
          }
        </div>
        
        <!-- 3. COMUNE (Filtered by Province) -->
        <div class="field col-12 md:col-4">
          <label for="comune" class="font-semibold block mb-2">
            COMUNE <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="comune"
            [options]="municipalityOptions()"
            [(ngModel)]="municipality"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona Comune --"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full"
            (ngModelChange)="onLegalAddressValueChange()"
            (blur)="markFieldTouched('municipality')"
            [attr.aria-required]="true"
            [attr.aria-disabled]="isMunicipalityDisabled()"
            [attr.aria-invalid]="isFieldTouched('municipality') && !municipality()"
            [attr.aria-describedby]="isFieldTouched('municipality') && !municipality() ? 'comune-error' : null"
          />
          @if (isFieldTouched('municipality') && !municipality()) {
            <small id="comune-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
          @if (isMunicipalityDisabled()) {
            <small class="text-color-secondary">Seleziona prima la provincia</small>
          }
        </div>
      </div>
      </div>

      <!-- Sottosezione: Contatti Istituzionali -->
      <div class="surface-0 border-round p-4 mb-2 border-1 border-200">
        <h3 class="text-lg font-semibold m-0 mb-4">
          <i class="pi pi-envelope mr-2 text-primary" aria-hidden="true"></i>
          Contatti Istituzionali
        </h3>

        <div class="grid">
          <div class="field col-12 md:col-4">
            <label for="email-registrazione" class="font-semibold block mb-2">EMAIL DI REGISTRAZIONE</label>
            <input
              id="email-registrazione"
              type="email"
              pInputText
              [value]="preRegEmail()"
              [disabled]="true"
              [attr.aria-readonly]="true"
              class="w-full"
            />
          </div>

          <div class="field col-12" role="radiogroup" aria-labelledby="institutional-email-same-label">
            <span id="institutional-email-same-label" class="font-semibold block mb-2">
              L'email istituzionale coincide con l'email inserita in fase di registrazione?
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </span>
            <div class="flex align-items-center gap-4">
              <div class="flex align-items-center gap-2">
                <p-radiobutton
                  name="institutionalEmailSameAsRegistrationEmail"
                  [value]="true"
                  [ngModel]="institutionalEmailSameAsRegistrationEmail()"
                  (ngModelChange)="onInstitutionalEmailSameAsRegistrationEmailChange($event)"
                  inputId="institutional-email-same-si"
                  [disabled]="isReadOnly()"
                  ariaLabel="Sì"
                />
                <label for="institutional-email-same-si">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton
                  name="institutionalEmailSameAsRegistrationEmail"
                  [value]="false"
                  [ngModel]="institutionalEmailSameAsRegistrationEmail()"
                  (ngModelChange)="onInstitutionalEmailSameAsRegistrationEmailChange($event)"
                  inputId="institutional-email-same-no"
                  [disabled]="isReadOnly()"
                  ariaLabel="No"
                />
                <label for="institutional-email-same-no">No</label>
              </div>
            </div>
          </div>

          <div class="field col-12 md:col-4">
            <label for="email-istituzionale" class="font-semibold block mb-2">
              EMAIL ISTITUZIONALE <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="email-istituzionale"
              type="email"
              pInputText
              [(ngModel)]="email"
              placeholder="esempio@email.it"
              class="w-full"
              [disabled]="isReadOnly() || institutionalEmailSameAsRegistrationEmail()"
              (blur)="markFieldTouched('email')"
              [attr.aria-required]="true"
              [attr.aria-invalid]="isFieldTouched('email') && (isMissingMeaningfulText(email()) || !isValidEmail(email()))"
              [attr.aria-describedby]="isFieldTouched('email') && (isMissingMeaningfulText(email()) || !isValidEmail(email())) ? 'email-istituzionale-error' : null"
              autocomplete="email"
              inputmode="email"
              maxlength="50"
            />
            @if (isFieldTouched('email') && isMissingMeaningfulText(email())) {
              <small id="email-istituzionale-error" class="text-red-500" role="alert">Campo obbligatorio</small>
            } @else if (isFieldTouched('email') && !isValidEmail(email())) {
              <small id="email-istituzionale-error" class="text-red-500" role="alert">Formato email non valido</small>
            }
          </div>

          <div class="field col-12 md:col-4">
            <label for="pec-istituzionale" class="font-semibold block mb-2">
              PEC ISTITUZIONALE <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="pec-istituzionale"
              type="email"
              pInputText
              [(ngModel)]="certifiedEmail"
              placeholder="esempio@pec.it"
              class="w-full"
              [disabled]="isReadOnly()"
              (blur)="markFieldTouched('certifiedEmail')"
              [attr.aria-required]="true"
              [attr.aria-invalid]="isFieldTouched('certifiedEmail') && (isMissingMeaningfulText(certifiedEmail()) || !isValidEmail(certifiedEmail()))"
              [attr.aria-describedby]="isFieldTouched('certifiedEmail') && (isMissingMeaningfulText(certifiedEmail()) || !isValidEmail(certifiedEmail())) ? 'pec-istituzionale-error' : null"
              autocomplete="email"
              inputmode="email"
              maxlength="50"
            />
            @if (isFieldTouched('certifiedEmail') && isMissingMeaningfulText(certifiedEmail())) {
              <small id="pec-istituzionale-error" class="text-red-500" role="alert">Campo obbligatorio</small>
            } @else if (isFieldTouched('certifiedEmail') && !isValidEmail(certifiedEmail())) {
              <small id="pec-istituzionale-error" class="text-red-500" role="alert">Formato email non valido</small>
            }
          </div>

          <div class="field col-12 md:col-4">
            <app-phone-input
              inputId="cellulare-istituzionale"
              label="CELLULARE ISTITUZIONALE"
              [(prefix)]="mobilePhonePrefix"
              [(number)]="mobilePhone"
              [required]="false"
              [disabled]="isReadOnly()"
              [forceShowError]="formSubmitted()"
              placeholder="es. 3351234567"
            />
          </div>

          <div class="field col-12 md:col-4">
            <app-phone-input
              inputId="telefono-istituzionale"
              label="TELEFONO ISTITUZIONALE"
              [(prefix)]="telephonePrefix"
              [(number)]="telephone"
              [required]="true"
              [disabled]="isReadOnly()"
              [forceShowError]="formSubmitted()"
              placeholder="es. 0612345678"
            />
          </div>

          <div class="field col-12 md:col-4">
            <label for="sito-internet-istituzionale" class="font-semibold block mb-2">
              SITO INTERNET ISTITUZIONALE
            </label>
            <input
              id="sito-internet-istituzionale"
              type="url"
              pInputText
              [(ngModel)]="website"
              placeholder="www.esempio.it"
              class="w-full"
              [disabled]="isReadOnly()"
              [attr.aria-required]="false"
              autocomplete="url"
              inputmode="url"
              maxlength="100"
            />
          </div>
        </div>
      </div>
      }
    </section>

    @if (!isOperationalLocationMode()) {
    <!-- SEZIONE 2: DATI RAPPRESENTANTE LEGALE -->
    <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-rappresentante">
      <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
        <i class="pi pi-user text-2xl" style="color: #00125b;" aria-hidden="true" title="Icona Rappresentante Legale"></i>
        <h2 id="section-rappresentante" class="text-xl font-semibold m-0" style="color: #00125b;">Dati Rappresentante Legale</h2>
      </div>

      <!-- Sottosezione: Anagrafica Rappresentante Legale -->
      <div class="surface-0 border-round p-4 mb-4 border-1 border-200">
        <h3 class="text-lg font-semibold m-0 mb-4">
          <i class="pi pi-id-card mr-2 text-primary" aria-hidden="true"></i>
          Anagrafica Rappresentante Legale
        </h3>

        <div class="grid">
          <div class="field col-12 md:col-4">
            <label for="cognome-rappresentante" class="font-semibold block mb-2">
              COGNOME RAPPRESENTANTE LEGALE <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="cognome-rappresentante"
              type="text"
              pInputText
              [(ngModel)]="lastname"
              placeholder="Descrizione libera"
              class="w-full"
              [disabled]="isReadOnly()"
              (blur)="markFieldTouched('lastname')"
              [attr.aria-required]="true"
              [attr.aria-invalid]="isFieldTouched('lastname') && isMissingMeaningfulText(lastname())"
              [attr.aria-describedby]="isFieldTouched('lastname') && isMissingMeaningfulText(lastname()) ? 'cognome-rappresentante-error' : null"
              autocomplete="family-name"
            />
            @if (isFieldTouched('lastname') && isMissingMeaningfulText(lastname())) {
              <small id="cognome-rappresentante-error" class="text-red-500" role="alert">Campo obbligatorio</small>
            }
          </div>

          <div class="field col-12 md:col-4">
            <label for="nome-rappresentante" class="font-semibold block mb-2">
              NOME RAPPRESENTANTE LEGALE <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="nome-rappresentante"
              type="text"
              pInputText
              [(ngModel)]="firstname"
              placeholder="Descrizione libera"
              class="w-full"
              [disabled]="isReadOnly()"
              (blur)="markFieldTouched('firstname')"
              [attr.aria-required]="true"
              [attr.aria-invalid]="isFieldTouched('firstname') && isMissingMeaningfulText(firstname())"
              [attr.aria-describedby]="isFieldTouched('firstname') && isMissingMeaningfulText(firstname()) ? 'nome-rappresentante-error' : null"
              autocomplete="given-name"
            />
            @if (isFieldTouched('firstname') && isMissingMeaningfulText(firstname())) {
              <small id="nome-rappresentante-error" class="text-red-500" role="alert">Campo obbligatorio</small>
            }
          </div>

          @if (!isAxaDeutscheBankBodyshop()) {
          <div class="field col-12 md:col-4">
            <label for="comune-nascita" class="font-semibold block mb-2">
              COMUNE DI NASCITA <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="comune-nascita"
              type="text"
              pInputText
              [(ngModel)]="birthMunicipality"
              placeholder="Descrizione libera"
              class="w-full"
              [disabled]="isReadOnly()"
              (blur)="markFieldTouched('birthMunicipality')"
              [attr.aria-required]="true"
              [attr.aria-invalid]="isFieldTouched('birthMunicipality') && isMissingMeaningfulText(birthMunicipality())"
              [attr.aria-describedby]="isFieldTouched('birthMunicipality') && isMissingMeaningfulText(birthMunicipality()) ? 'comune-nascita-error' : null"
              autocomplete="off"
            />
            @if (isFieldTouched('birthMunicipality') && isMissingMeaningfulText(birthMunicipality())) {
              <small id="comune-nascita-error" class="text-red-500" role="alert">Campo obbligatorio</small>
            }
          </div>

          <div class="field col-12 md:col-4">
            <label for="data-nascita" class="font-semibold block mb-2">
              DATA DI NASCITA <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <p-datepicker
              id="data-nascita"
              [(ngModel)]="dateOfBirth"
              dateFormat="dd/mm/yy"
              [maxDate]="today"
              [showIcon]="true"
              [disabled]="isReadOnly()"
              placeholder="Seleziona data"
              styleClass="w-full"
              (blur)="markFieldTouched('dateOfBirth')"
              [attr.aria-required]="true"
              [attr.aria-invalid]="isFieldTouched('dateOfBirth') && !dateOfBirth()"
              [attr.aria-describedby]="isFieldTouched('dateOfBirth') && !dateOfBirth() ? 'data-nascita-error' : null"
            />
            @if (isFieldTouched('dateOfBirth') && !dateOfBirth()) {
              <small id="data-nascita-error" class="text-red-500" role="alert">Campo obbligatorio</small>
            }
          </div>
          }

          <div class="field col-12 md:col-4">
            <label for="codice-fiscale-rappresentante" class="font-semibold block mb-2">
              CODICE FISCALE <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="codice-fiscale-rappresentante"
              type="text"
              pInputText
              [value]="legalRepresentativeFiscalCode()"
              (input)="onLegalRepTaxCodeChange($any($event.target).value)"
              (blur)="markFieldTouched('legalRepresentativeFiscalCode')"
              placeholder="es. RSSMRA80A01H501U"
              maxlength="16"
              [disabled]="isReadOnly()"
              [class.p-invalid]="legalRepresentativeFiscalCodeError() || (isFieldTouched('legalRepresentativeFiscalCode') && isMissingMeaningfulText(legalRepresentativeFiscalCode()))"
              [attr.aria-invalid]="legalRepresentativeFiscalCodeError() || (isFieldTouched('legalRepresentativeFiscalCode') && isMissingMeaningfulText(legalRepresentativeFiscalCode())) ? 'true' : null"
              [attr.aria-describedby]="legalRepresentativeFiscalCodeError() || (isFieldTouched('legalRepresentativeFiscalCode') && isMissingMeaningfulText(legalRepresentativeFiscalCode())) ? 'codice-fiscale-rapp-error' : null"
              [attr.aria-required]="true"
              class="w-full"
            />
            @if (isFieldTouched('legalRepresentativeFiscalCode') && isMissingMeaningfulText(legalRepresentativeFiscalCode())) {
              <small id="codice-fiscale-rapp-error" class="text-red-500" role="alert">Campo obbligatorio</small>
            } @else if (legalRepresentativeFiscalCodeError()) {
              <small id="codice-fiscale-rapp-error" class="p-error" role="alert" aria-live="polite">
                {{ legalRepresentativeFiscalCodeError() }}
              </small>
            }
          </div>
        </div>
      </div>

      <!-- Sottosezione: Contatti Rappresentante Legale -->
      <div class="surface-0 border-round p-4 mb-2 border-1 border-200">
        <h3 class="text-lg font-semibold m-0 mb-4">
          <i class="pi pi-phone mr-2 text-primary" aria-hidden="true"></i>
          Contatti Rappresentante Legale
        </h3>

        <div class="grid">
          <div class="field col-12" role="radiogroup" aria-labelledby="responsible-email-same-label">
            <span id="responsible-email-same-label" class="font-semibold block mb-2">
              L'email del rappresentante legale coincide con l'email inserita in fase di registrazione?
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </span>
            <div class="flex align-items-center gap-4">
              <div class="flex align-items-center gap-2">
                <p-radiobutton
                  name="responsibleEmailSameAsRegistrationEmail"
                  [value]="true"
                  [ngModel]="responsibleEmailSameAsRegistrationEmail()"
                  (ngModelChange)="onResponsibleEmailSameAsRegistrationEmailChange($event)"
                  inputId="responsible-email-same-si"
                  [disabled]="isReadOnly()"
                  ariaLabel="Sì"
                />
                <label for="responsible-email-same-si">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton
                  name="responsibleEmailSameAsRegistrationEmail"
                  [value]="false"
                  [ngModel]="responsibleEmailSameAsRegistrationEmail()"
                  (ngModelChange)="onResponsibleEmailSameAsRegistrationEmailChange($event)"
                  inputId="responsible-email-same-no"
                  [disabled]="isReadOnly()"
                  ariaLabel="No"
                />
                <label for="responsible-email-same-no">No</label>
              </div>
            </div>
          </div>

          <div class="field col-12 md:col-4">
            <label for="email-rappresentante" class="font-semibold block mb-2">
              EMAIL RAPPRESENTANTE LEGALE <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="email-rappresentante"
              type="email"
              pInputText
              [(ngModel)]="responsibleEmail"
              placeholder="esempio@email.it"
              class="w-full"
              [disabled]="isReadOnly() || responsibleEmailSameAsRegistrationEmail()"
              (blur)="markFieldTouched('responsibleEmail')"
              [attr.aria-required]="true"
              [attr.aria-invalid]="isFieldTouched('responsibleEmail') && (isMissingMeaningfulText(responsibleEmail()) || !isValidEmail(responsibleEmail()))"
              [attr.aria-describedby]="isFieldTouched('responsibleEmail') && (isMissingMeaningfulText(responsibleEmail()) || !isValidEmail(responsibleEmail())) ? 'email-rappresentante-error' : null"
              autocomplete="email"
              inputmode="email"
              maxlength="60"
            />
            @if (isFieldTouched('responsibleEmail') && isMissingMeaningfulText(responsibleEmail())) {
              <small id="email-rappresentante-error" class="text-red-500" role="alert">Campo obbligatorio</small>
            } @else if (isFieldTouched('responsibleEmail') && !isValidEmail(responsibleEmail())) {
              <small id="email-rappresentante-error" class="text-red-500" role="alert">Formato email non valido</small>
            }
          </div>

          <div class="field col-12 md:col-4">
            <app-phone-input
              inputId="cellulare-rappresentante"
              label="CELLULARE RAPPRESENTANTE LEGALE"
              [prefix]="mobilePhoneResponsiblePrefix()"
              (prefixChange)="onRepresentativeMobileChange(mobilePhoneResponsible(), $event)"
              [number]="mobilePhoneResponsible()"
              (numberChange)="onRepresentativeMobileChange($event, mobilePhoneResponsiblePrefix())"
              [required]="true"
              [disabled]="isReadOnly()"
              [forceShowError]="formSubmitted()"
              placeholder="es. 3351234567"
            />
          </div>

          <div class="field col-12 md:col-4">
            <app-phone-input
              inputId="telefono-rappresentante"
              label="TELEFONO RAPPRESENTANTE LEGALE"
              [(prefix)]="ownerTelephonePrefix"
              [(number)]="ownerTelephone"
              [disabled]="isReadOnly()"
              [forceShowError]="formSubmitted()"
              placeholder="es. 0612345678"
            />
          </div>
          <div class="field col-12 md:col-4">
            <app-phone-input
              inputId="claims-network-phone-rappresentante"
              label="Numero di cellulare/telefono riservato alla rete liquidativa"
              description="Numero riservato alla compagnia per comunicazioni urgenti"
              [prefix]="claimsNetworkPhonePrefix()"
              (prefixChange)="onClaimsNetworkPhoneChange(claimsNetworkPhone(), $event)"
              [number]="claimsNetworkPhone()"
              (numberChange)="onClaimsNetworkPhoneChange($event, claimsNetworkPhonePrefix())"
              [disabled]="isReadOnly()"
              [forceShowError]="formSubmitted()"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- SEZIONE 3: RECAPITI BANCARI E FISCALI -->
    <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-bancari">
      <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
        <i class="pi pi-credit-card text-2xl" style="color: #00125b;" aria-hidden="true" title="Icona Recapiti Bancari"></i>
        <h2 id="section-bancari" class="text-xl font-semibold m-0" style="color: #00125b;">Recapiti Bancari e Fiscali</h2>
        </div>

      <div class="grid">
        @if (!isAxaDeutscheBankBodyshop()) {
        <!-- DATI FATTURAZIONE -->
        <div class="field col-12 md:col-4">
          <label for="ragione-sociale-fattura" class="font-semibold block mb-2">
            RAGIONE SOCIALE / SOGGETTO FATTURA <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="ragione-sociale-fattura"
            type="text"
            pInputText
            [(ngModel)]="invoiceParty"
            placeholder="Descrizione libera"
            class="w-full"
            (blur)="markFieldTouched('invoiceParty')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('invoiceParty') && (!invoiceParty() || invoiceParty().trim() === '')"
            [attr.aria-describedby]="isFieldTouched('invoiceParty') && (!invoiceParty() || invoiceParty().trim() === '') ? 'ragione-sociale-fattura-error' : null"
            autocomplete="organization"
          />
          @if (isFieldTouched('invoiceParty') && (!invoiceParty() || invoiceParty().trim() === '')) {
            <small id="ragione-sociale-fattura-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        @if (hasMeaningfulText(vatNumber())) {
          <div class="field col-12" role="radiogroup" aria-labelledby="invoice-vat-same-label">
            <span id="invoice-vat-same-label" class="font-semibold block mb-2">
              La Partita IVA per la fatturazione coincide con la Partita IVA anagrafica? <span class="text-red-500" aria-hidden="true">*</span>
            </span>
            <div class="flex align-items-center gap-4">
              <div class="flex align-items-center gap-2">
                <p-radiobutton
                  name="invoiceVatNumberSameAsVatNumber"
                  [value]="true"
                  [ngModel]="invoiceVatNumberSameAsVatNumber()"
                  (ngModelChange)="onInvoiceVatNumberSameAsVatNumberChange($event)"
                  inputId="invoice-vat-same-si"
                  [disabled]="isReadOnly()"
                  ariaLabel="Sì"
                />
                <label for="invoice-vat-same-si">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton
                  name="invoiceVatNumberSameAsVatNumber"
                  [value]="false"
                  [ngModel]="invoiceVatNumberSameAsVatNumber()"
                  (ngModelChange)="onInvoiceVatNumberSameAsVatNumberChange($event)"
                  inputId="invoice-vat-same-no"
                  [disabled]="isReadOnly()"
                  ariaLabel="No"
                />
                <label for="invoice-vat-same-no">No</label>
              </div>
            </div>
          </div>
        }
        <div class="field col-12 md:col-4">
          <label for="partita-iva-fattura" class="font-semibold block mb-2">
            PARTITA IVA FATTURA <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="partita-iva-fattura"
            type="text"
            pInputText
            [(ngModel)]="invoiceVatNumber"
            (ngModelChange)="onInvoiceVatNumberChange($event)"
            placeholder="es. 12345678901"
            class="w-full"
            [disabled]="isReadOnly() || (hasMeaningfulText(vatNumber()) && invoiceVatNumberSameAsVatNumber())"
            (blur)="markFieldTouched('invoiceVatNumber')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('invoiceVatNumber') && (!invoiceVatNumber() || invoiceVatNumber().trim() === '')"
            [attr.aria-describedby]="'partita-iva-fattura-hint ' + (isFieldTouched('invoiceVatNumber') && (!invoiceVatNumber() || invoiceVatNumber().trim() === '') ? 'partita-iva-fattura-error' : '')"
            autocomplete="off"
            inputmode="numeric"
            pattern="[0-9]{11}"
            maxlength="11"
          />
          <small id="partita-iva-fattura-hint" class="text-color-secondary text-xs block mt-1">
            Inserire 11 cifre numeriche.
          </small>
          @if (isFieldTouched('invoiceVatNumber') && (!invoiceVatNumber() || invoiceVatNumber().trim() === '')) {
            <small id="partita-iva-fattura-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>

        <div class="field col-12" role="radiogroup" aria-labelledby="invoice-address-same-label">
          <span id="invoice-address-same-label" class="font-semibold block mb-2">
            L'indirizzo di fatturazione coincide con l'indirizzo della sede legale?
            <span class="text-red-500" aria-hidden="true">*</span>
          </span>
          <div class="flex align-items-center gap-4">
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="invoiceAddressSameAsLegalAddress"
                [value]="true"
                [ngModel]="invoiceAddressSameAsLegalAddress()"
                (ngModelChange)="onInvoiceAddressSameAsLegalAddressChange($event)"
                inputId="invoice-address-same-si"
                [disabled]="isReadOnly()"
                ariaLabel="Sì"
              />
              <label for="invoice-address-same-si">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="invoiceAddressSameAsLegalAddress"
                [value]="false"
                [ngModel]="invoiceAddressSameAsLegalAddress()"
                (ngModelChange)="onInvoiceAddressSameAsLegalAddressChange($event)"
                inputId="invoice-address-same-no"
                [disabled]="isReadOnly()"
                ariaLabel="No"
              />
              <label for="invoice-address-same-no">No</label>
            </div>
          </div>
        </div>

        <!-- INDIRIZZO FATTURAZIONE -->
        <div class="field col-12 md:col-4">
          <label for="tipo-via-fatturazione" class="font-semibold block mb-2">
            TIPO VIA (FATTURAZIONE) <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="tipo-via-fatturazione"
            [options]="streetTypeOptions()"
            [(ngModel)]="invoiceStreetType"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona --"
            [showClear]="true"
            styleClass="w-full"
            [disabled]="isReadOnly() || invoiceAddressSameAsLegalAddress()"
            (ngModelChange)="onInvoiceAddressFieldChange()"
            (blur)="markFieldTouched('invoiceStreetType')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('invoiceStreetType') && !invoiceStreetType()"
            [attr.aria-describedby]="isFieldTouched('invoiceStreetType') && !invoiceStreetType() ? 'tipo-via-fatturazione-error' : null"
          />
          @if (isFieldTouched('invoiceStreetType') && !invoiceStreetType()) {
            <small id="tipo-via-fatturazione-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        <div class="field col-12 md:col-4">
          <label for="indirizzo-fattura" class="font-semibold block mb-2">
            INDIRIZZO (FATTURAZIONE) <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="indirizzo-fattura"
            type="text"
            pInputText
            [(ngModel)]="invoiceAddress"
            placeholder="Descrizione libera"
            class="w-full"
            [disabled]="isReadOnly() || invoiceAddressSameAsLegalAddress()"
            (ngModelChange)="onInvoiceAddressFieldChange()"
            (blur)="markFieldTouched('invoiceAddress')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('invoiceAddress') && (!invoiceAddress() || invoiceAddress().trim() === '')"
            [attr.aria-describedby]="isFieldTouched('invoiceAddress') && (!invoiceAddress() || invoiceAddress().trim() === '') ? 'indirizzo-fattura-error' : null"
            autocomplete="street-address"
          />
          @if (isFieldTouched('invoiceAddress') && (!invoiceAddress() || invoiceAddress().trim() === '')) {
            <small id="indirizzo-fattura-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        <div class="field col-12 md:col-4">
          <label for="numero-fatturazione" class="font-semibold block mb-2">
            CIVICO (FATTURAZIONE) <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="numero-fatturazione"
            type="text"
            pInputText
            [(ngModel)]="invoiceStreetNumber"
            placeholder="es. 7/A"
            class="w-full"
            [disabled]="isReadOnly() || invoiceAddressSameAsLegalAddress()"
            (ngModelChange)="onInvoiceAddressFieldChange()"
            (blur)="markFieldTouched('invoiceStreetNumber')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('invoiceStreetNumber') && !invoiceStreetNumber()"
            [attr.aria-describedby]="isFieldTouched('invoiceStreetNumber') && !invoiceStreetNumber() ? 'numero-fatturazione-error' : null"
          />
          @if (isFieldTouched('invoiceStreetNumber') && !invoiceStreetNumber()) {
            <small id="numero-fatturazione-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        <div class="field col-12 md:col-4">
          <label for="cap-fatturazione" class="font-semibold block mb-2">
            CAP (FATTURAZIONE) <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            type="text"
            id="cap-fatturazione"
            pInputText
            [(ngModel)]="invoiceZipCode"
            maxlength="5"
            placeholder="5 cifre"
            class="w-full"
            [disabled]="isReadOnly() || invoiceAddressSameAsLegalAddress()"
            (ngModelChange)="onInvoiceAddressFieldChange()"
            (blur)="markFieldTouched('invoiceZipCode')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('invoiceZipCode') && !invoiceZipCode()"
            [attr.aria-describedby]="isFieldTouched('invoiceZipCode') && !invoiceZipCode() ? 'cap-fatturazione-error' : null"
          />
          @if (isFieldTouched('invoiceZipCode') && !invoiceZipCode()) {
            <small id="cap-fatturazione-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>

        <!-- Geographic hierarchy: Region ? Province ? Municipality -->
        <div class="field col-12 md:col-4">
          <label for="regione-fatturazione" class="font-semibold block mb-2">
            REGIONE (FATTURAZIONE) <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="regione-fatturazione"
            [options]="invoiceRegionOptions()"
            [(ngModel)]="invoiceRegion"
            (ngModelChange)="onInvoiceRegionChange($event); onInvoiceAddressFieldChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona Regione --"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full"
            [disabled]="isReadOnly() || invoiceAddressSameAsLegalAddress()"
            (blur)="markFieldTouched('invoiceRegion')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('invoiceRegion') && !invoiceRegion()"
            [attr.aria-describedby]="isFieldTouched('invoiceRegion') && !invoiceRegion() ? 'regione-fatturazione-error' : null"
          />
          @if (isFieldTouched('invoiceRegion') && !invoiceRegion()) {
            <small id="regione-fatturazione-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>

        <div class="field col-12 md:col-4">
          <label for="provincia-fatturazione" class="font-semibold block mb-2">
            PROVINCIA (FATTURAZIONE) <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="provincia-fatturazione"
            [options]="invoiceProvinceOptions()"
            [(ngModel)]="invoiceProvince"
            (ngModelChange)="onInvoiceProvinceChange($event); onInvoiceAddressFieldChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona Provincia --"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full"
            [disabled]="isReadOnly() || invoiceAddressSameAsLegalAddress() || isInvoiceProvinceDisabled()"
            (blur)="markFieldTouched('invoiceProvince')"
            [attr.aria-required]="true"
            [attr.aria-disabled]="isInvoiceProvinceDisabled()"
            [attr.aria-invalid]="isFieldTouched('invoiceProvince') && !invoiceProvince()"
            [attr.aria-describedby]="isFieldTouched('invoiceProvince') && !invoiceProvince() ? 'provincia-fatturazione-error' : null"
          />
          @if (isFieldTouched('invoiceProvince') && !invoiceProvince()) {
            <small id="provincia-fatturazione-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
          @if (isInvoiceProvinceDisabled()) {
            <small class="text-color-secondary">Seleziona prima la regione</small>
          }
        </div>

        <div class="field col-12 md:col-4">
          <label for="comune-fatturazione" class="font-semibold block mb-2">
            COMUNE (FATTURAZIONE) <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="comune-fatturazione"
            [options]="invoiceMunicipalityOptions()"
            [(ngModel)]="invoiceMunicipality"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona Comune --"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full"
            [disabled]="isReadOnly() || invoiceAddressSameAsLegalAddress() || isInvoiceMunicipalityDisabled()"
            (ngModelChange)="onInvoiceAddressFieldChange()"
            (blur)="markFieldTouched('invoiceMunicipality')"
            [attr.aria-required]="true"
            [attr.aria-disabled]="isInvoiceMunicipalityDisabled()"
            [attr.aria-invalid]="isFieldTouched('invoiceMunicipality') && !invoiceMunicipality()"
            [attr.aria-describedby]="isFieldTouched('invoiceMunicipality') && !invoiceMunicipality() ? 'comune-fatturazione-error' : null"
          />
          @if (isFieldTouched('invoiceMunicipality') && !invoiceMunicipality()) {
            <small id="comune-fatturazione-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
          @if (isInvoiceMunicipalityDisabled()) {
            <small class="text-color-secondary">Seleziona prima la provincia</small>
          }
        </div>
        }
        
        <!-- DATI FISCALI -->
        <div class="field col-12">
          @if (isAxaDeutscheBankBodyshop()) {
          <label for="regime-fiscale" class="font-semibold block mb-2">
            REGIME FISCALE APPLICATO <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="regime-fiscale"
            [options]="fiscalRegimesOptions()"
            [(ngModel)]="fiscalRegime"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona --"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full"
            (blur)="markFieldTouched('fiscalRegime')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('fiscalRegime') && !fiscalRegime()"
            [attr.aria-describedby]="isFieldTouched('fiscalRegime') && !fiscalRegime() ? 'regime-fiscale-error' : null"
          />
          @if (isFieldTouched('fiscalRegime') && !fiscalRegime()) {
            <small id="regime-fiscale-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
          } @else {
          <label for="regime-fiscale" class="font-semibold block mb-2">
            REGIME FISCALE APPLICATO <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            id="regime-fiscale"
            [options]="fiscalRegimesOptions()"
            [(ngModel)]="fiscalRegime"
            optionLabel="label"
            optionValue="value"
            placeholder="-- Seleziona --"
            [filter]="true"
            [showClear]="true"
            styleClass="w-full"
            (blur)="markFieldTouched('fiscalRegime')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('fiscalRegime') && !fiscalRegime()"
            [attr.aria-describedby]="isFieldTouched('fiscalRegime') && !fiscalRegime() ? 'regime-fiscale-error' : null"
          />
          @if (isFieldTouched('fiscalRegime') && !fiscalRegime()) {
            <small id="regime-fiscale-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
          }
        </div>

        <!-- DATI BANCARI -->
        <div class="field col-12 md:col-4">
          <label for="iban" class="font-semibold block mb-2">
            IBAN <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="iban"
            type="text"
            pInputText
            [value]="iban()"
            (input)="onIbanInput($event)"
            placeholder="es. IT60 X05428 11101 000000123456"
            class="w-full"
            (blur)="onIbanBlur($event); markFieldTouched('iban')"
            [class.p-invalid]="isFieldTouched('iban') && ((!iban() || iban().trim() === '') || ibanFormatError() || ibanInvalidCharacterError())"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('iban') && ((!iban() || iban().trim() === '') || ibanFormatError() || ibanInvalidCharacterError())"
            [attr.aria-describedby]="'iban-hint ' + (isFieldTouched('iban') && ((!iban() || iban().trim() === '') || ibanFormatError() || ibanInvalidCharacterError()) ? 'iban-error' : '')"
            autocomplete="off"
            inputmode="text"
          />
          <small id="iban-hint" class="text-color-secondary text-xs block mt-1">
            @if (professionalsWizardService.isPublicEmployeeDoctor()) {
              Inserire i dati bancari e ragione sociale dell'ente pubblico di riferimento.
            } @else {
              Le ricordiamo che l'IBAN inserito deve appartenere alla Società/persona inserita.
            }
            <br />
            IBAN italiano: IT + 2 cifre + 1 lettera + 5 cifre + 5 cifre + 12 caratteri (27 caratteri in totale).
          </small>
          @if (isFieldTouched('iban') && (!iban() || iban().trim() === '')) {
            <small id="iban-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          } @else if (isFieldTouched('iban') && ibanInvalidCharacterError()) {
            <small id="iban-error" class="text-red-500" role="alert">
              Sono ammessi solo lettere, numeri e spazi.
            </small>
          } @else if (isFieldTouched('iban') && ibanFormatError()) {
            <small id="iban-error" class="text-red-500" role="alert">
              <i class="pi pi-times-circle mr-1" aria-hidden="true"></i>Verificare la correttezza del codice IBAN inserito.
            </small>
          }
        </div>
        @if (!isAxaDeutscheBankBodyshop()) {
        <div class="field col-12 md:col-4">
          <label for="swift" class="font-semibold block mb-2">
            SWIFT <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="swift"
            type="text"
            pInputText
            [ngModel]="swift()"
            (ngModelChange)="onSwiftChange($event)"
            placeholder="es. ABCDITMMXXX"
            class="w-full"
            (blur)="markFieldTouched('swift')"
            [class.p-invalid]="isFieldTouched('swift') && ((!swift() || swift().trim() === '') || swiftFormatError())"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('swift') && ((!swift() || swift().trim() === '') || swiftFormatError())"
            [attr.aria-describedby]="'swift-hint ' + (isFieldTouched('swift') && (!swift() || swift().trim() === '') ? 'swift-error' : (isFieldTouched('swift') && swiftFormatError() ? 'swift-format-error' : ''))"
            autocomplete="off"
            inputmode="text"
            maxlength="11"
          />
          <small id="swift-hint" class="text-color-secondary text-xs block mt-1">
            Esempio: ABCDITMM oppure ABCDITMMXXX (8 o 11 caratteri).
          </small>
          @if (isFieldTouched('swift') && (!swift() || swift().trim() === '')) {
            <small id="swift-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          } @else if (isFieldTouched('swift') && swiftFormatError()) {
            <small id="swift-format-error" class="text-red-500" role="alert">
              <i class="pi pi-times-circle mr-1" aria-hidden="true"></i>Formato SWIFT/BIC non valido. Inserire 8 o 11 caratteri alfanumerici.
            </small>
          }
        </div>
        }
        <div class="field col-12 md:col-4">
          <label for="istituto-credito" class="font-semibold block mb-2">
            ISTITUTO DI CREDITO <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="istituto-credito"
            type="text"
            pInputText
            [(ngModel)]="bankInstitutionName"
            placeholder="Descrizione libera"
            class="w-full"
            (blur)="markFieldTouched('bankInstitutionName')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('bankInstitutionName') && (!bankInstitutionName() || bankInstitutionName().trim() === '')"
            [attr.aria-describedby]="isFieldTouched('bankInstitutionName') && (!bankInstitutionName() || bankInstitutionName().trim() === '') ? 'istituto-credito-error' : null"
            autocomplete="off"
          />
          @if (isFieldTouched('bankInstitutionName') && (!bankInstitutionName() || bankInstitutionName().trim() === '')) {
            <small id="istituto-credito-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        <div class="field col-12 md:col-4">
          <label for="filiale-di" class="font-semibold block mb-2">
            {{ isAxaDeutscheBankBodyshop() ? 'FILIALE' : 'FILIALE DI' }} <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="filiale-di"
            type="text"
            pInputText
            [(ngModel)]="bankInstitutionBranch"
            placeholder="Descrizione libera"
            class="w-full"
            (blur)="markFieldTouched('bankInstitutionBranch')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldTouched('bankInstitutionBranch') && (!bankInstitutionBranch() || bankInstitutionBranch().trim() === '')"
            [attr.aria-describedby]="isFieldTouched('bankInstitutionBranch') && (!bankInstitutionBranch() || bankInstitutionBranch().trim() === '') ? 'filiale-di-error' : null"
            autocomplete="address-level2"
          />
          @if (isFieldTouched('bankInstitutionBranch') && (!bankInstitutionBranch() || bankInstitutionBranch().trim() === '')) {
            <small id="filiale-di-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          }
        </div>
        <div class="field col-12 md:col-4">
          <label for="intestatario-cc" class="font-semibold block mb-2">
            INTESTATARIO C/C <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="intestatario-cc"
            type="text"
            pInputText
            [(ngModel)]="accountOwner"
            placeholder="Descrizione libera"
            class="w-full"
            (blur)="markFieldTouched('accountOwner')"
            [attr.aria-required]="true"
            [class.p-invalid]="(isFieldTouched('accountOwner') && isMissingMeaningfulText(accountOwner())) || accountOwnerMismatch()"
            [attr.aria-invalid]="((isFieldTouched('accountOwner') && isMissingMeaningfulText(accountOwner())) || accountOwnerMismatch()) ? 'true' : null"
            [attr.aria-describedby]="((isFieldTouched('accountOwner') && isMissingMeaningfulText(accountOwner())) || accountOwnerMismatch()) ? 'intestatario-cc-error' : null"
            autocomplete="name"
          />
          @if (isFieldTouched('accountOwner') && isMissingMeaningfulText(accountOwner())) {
            <small id="intestatario-cc-error" class="text-red-500" role="alert">Campo obbligatorio</small>
          } @else if (accountOwnerMismatch()) {
            <small id="intestatario-cc-error" class="text-red-500" role="alert">{{ getAccountOwnerMismatchMessage() }}</small>
          }
        </div>
      </div>
    </section>
    }

    <!-- Action Buttons (hidden in review mode where professionalIdInput is set) -->
    @if (!professionalIdInput()) {
    <div class="card-footer">
      <div class="action-buttons">
        <!-- Read-only mode: only show back button if not in review mode -->
        @if (isReadOnly()) {
          <button
            pButton
            type="button"
            label="Torna alla Progressione"
            icon="pi pi-arrow-left"
            class="p-button-secondary"
            (click)="backToProgression()"
            aria-label="Torna alla pagina di progressione della registrazione"
          ></button>
        }
        
        <!-- Editable mode: show normal action buttons -->
        @else {
          <button
            pButton
            type="button"
            label="Indietro"
            icon="pi pi-arrow-left"
            class="p-button-text"
            (click)="backToSteps()"
            aria-label="Torna alla pagina di selezione degli step"
          ></button>
          <button
            pButton
            type="button"
            label="Salva Bozza"
            icon="pi pi-save"
            class="p-button-outlined"
            (click)="onSaveDraft()"
            aria-label="Salva i dati generali come bozza senza validazione"
            pTooltip="Salva i dati inseriti come bozza. Non è richiesta la compilazione completa dei campi obbligatori."
            tooltipPosition="top"
          ></button>
          <button
            pButton
            type="button"
            label="Salva e continua"
            icon="pi pi-arrow-right"
            iconPos="right"
            class="p-button-primary"
            (click)="onSaveAndContinue()"
            aria-label="Salva i dati generali e procedi alla sezione successiva. Richiede la compilazione di tutti i campi obbligatori."
            pTooltip="Valida e salva i dati generali, poi prosegui. Tutti i campi obbligatori devono essere compilati correttamente."
            tooltipPosition="top"
          ></button>
        }
      </div>
    </div>
    }
  </div>
</div>




